import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import GoldText from '@/components/site/GoldText';
import { supabase } from '@/integrations/supabase/client';
import { business } from '@/content/business';
import { interestOptions, isInterestId, type InterestId } from '@/content/workflows';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn } from '@/lib/utils';

interface Details {
  name: string;
  email: string;
  businessName: string;
  interest: InterestId;
  workflow: string;
  website: string;
  currentTools: string;
  marketingConsent: boolean;
}

type DetailErrors = Partial<Record<'name' | 'email' | 'businessName' | 'workflow', string>>;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const TIMES = ['Morning (9am–12pm)', 'Midday (12pm–2pm)', 'Afternoon (2pm–5pm)'];

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const timezoneOptions = (current: string) => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const zones = intl.supportedValuesOf?.('timeZone') ?? [];
  return zones.includes(current) ? zones : [current, ...zones];
};

function validateDetails(d: Details): DetailErrors {
  const errors: DetailErrors = {};
  if (!d.name.trim()) errors.name = 'Enter your name.';
  if (!d.email.trim()) errors.email = 'Enter your work email.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()))
    errors.email = 'Enter a valid email address, like name@company.com.';
  if (!d.businessName.trim()) errors.businessName = 'Enter your business name.';
  if (!d.workflow.trim()) errors.workflow = 'Describe the task in a sentence or two.';
  return errors;
}

/** Build the scheduler URL with the visitor's name and email prefilled. */
function schedulerSrc(details: Details) {
  try {
    const url = new URL(business.schedulingUrl);
    url.searchParams.set('name', details.name.trim());
    url.searchParams.set('email', details.email.trim());
    return url.toString();
  } catch {
    return business.schedulingUrl;
  }
}

const inputClass =
  'w-full rounded-[2px] border border-rule2 bg-knightblack/80 px-4 py-3 text-softwhite placeholder:text-mist/60 focus:border-goldcore focus:outline-none focus:ring-1 focus:ring-goldcore aria-[invalid=true]:border-victorycoral';

const Field = ({
  id,
  label,
  optional,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div>
    <label htmlFor={id} className="mb-2 flex items-baseline justify-between text-sm font-medium">
      {label}
      {optional && <span className="font-normal text-mist">Optional</span>}
    </label>
    {children}
    {hint && !error && (
      <p id={`${id}-hint`} className="mt-1.5 text-sm text-mist">
        {hint}
      </p>
    )}
    {error && (
      <p id={`${id}-error`} className="mt-1.5 text-sm text-victorycoral">
        {error}
      </p>
    )}
  </div>
);

const describedBy = (id: string, error?: string, hint?: boolean) =>
  error ? `${id}-error` : hint ? `${id}-hint` : undefined;

const StepIndicator = ({ step }: { step: 1 | 2 }) => (
  <ol className="mb-10 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.16em]" aria-label="Progress">
    {['Your workflow', 'Choose a time'].map((label, i) => {
      const n = i + 1;
      const current = n === step;
      return (
        <li key={label} className="flex items-center gap-3" aria-current={current ? 'step' : undefined}>
          <span
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border text-[0.68rem]',
              n <= step ? 'border-goldcore text-goldcore' : 'border-rule2 text-mist',
            )}
          >
            {n}
          </span>
          <span className={current ? 'text-softwhite' : 'text-mist'}>{label}</span>
          {n === 1 && <span aria-hidden="true" className="h-px w-8 bg-rule2" />}
        </li>
      );
    })}
  </ol>
);

const Start = () => {
  usePageMeta('Start for free', 'Tell us about one workflow, then choose a time for your free discovery call.');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requested = params.get('interest');

  const [step, setStep] = useState<1 | 2>(1);
  const [details, setDetails] = useState<Details>({
    name: '',
    email: '',
    businessName: '',
    interest: isInterestId(requested) ? requested : 'not-sure',
    workflow: '',
    website: '',
    currentTools: '',
    marketingConsent: false,
  });
  const [errors, setErrors] = useState<DetailErrors>({});

  const detectedTz = useMemo(detectTimezone, []);
  const zones = useMemo(() => timezoneOptions(detectedTz), [detectedTz]);
  const [days, setDays] = useState<string[]>([]);
  const [times, setTimes] = useState<string[]>([]);
  const [timezone, setTimezone] = useState(detectedTz);
  const [notes, setNotes] = useState('');
  const [availabilityError, setAvailabilityError] = useState('');
  // Fall back to request mode if no scheduler is configured or the visitor opts out of it.
  const [useScheduler, setUseScheduler] = useState(!!business.schedulingUrl);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formTop = useRef<HTMLHeadingElement>(null);

  const set = <K extends keyof Details>(key: K, value: Details[K]) => {
    setDetails((d) => ({ ...d, [key]: value }));
    if (errors[key as keyof DetailErrors]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggle = (list: string[], value: string, setter: (v: string[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setAvailabilityError('');
  };

  const save = async (mode: 'request' | 'scheduler') => {
    const { error } = await supabase.from('discovery_requests').insert({
      name: details.name.trim(),
      email: details.email.trim(),
      business_name: details.businessName.trim(),
      interest: details.interest,
      workflow: details.workflow.trim(),
      website: details.website.trim() || null,
      current_tools: details.currentTools.trim() || null,
      marketing_consent: details.marketingConsent,
      scheduling_mode: mode,
      preferred_days: mode === 'request' ? days : null,
      preferred_times: mode === 'request' ? times : null,
      timezone: mode === 'request' ? timezone : null,
      availability_notes: mode === 'request' ? notes.trim() || null : null,
    });
    if (error) throw error;
  };

  const goToStep2 = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateDetails(details);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(first)?.focus();
      return;
    }
    setSubmitError('');
    // In booking mode the scheduler owns the appointment, so record the intake now.
    if (useScheduler) {
      setSubmitting(true);
      try {
        await save('scheduler');
      } catch {
        // Not blocking: the scheduler still captures name and email.
        console.error('Could not save discovery intake before scheduling');
      } finally {
        setSubmitting(false);
      }
    }
    setStep(2);
    requestAnimationFrame(() => formTop.current?.focus());
  };

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (days.length === 0 && times.length === 0 && !notes.trim()) {
      setAvailabilityError('Choose at least one day or time, or tell us what works in the notes.');
      document.getElementById('availability-days')?.focus();
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await save('request');
      navigate('/start/confirmed', {
        state: { mode: 'request', name: details.name.trim(), email: details.email.trim() },
      });
    } catch {
      setSubmitError(`We couldn’t send your request. Please try again, or email us at ${business.email}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-14 sm:px-6 md:pt-20">
      <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
        <div>
          <p className="eyebrow mb-5 block">Start for free</p>
          <h1 className="mb-6 text-[clamp(2.75rem,5vw,4.5rem)] leading-none">
            What would you like AI to <GoldText>take off your plate?</GoldText>
          </h1>
          <p className="body-copy mb-8">
            Tell us about one workflow you want to improve. Then choose a time for your free discovery call.
          </p>
          <div className="surface surface-lit p-5">
            <p className="font-serif text-2xl">Discovery is free.</p>
            <p className="text-mist">
              If a pilot makes sense, its scope and costs are confirmed before you commit. No payment details, no
              account needed.
            </p>
          </div>
          <dl className="mt-8 space-y-3 text-sm">
            <div className="grid grid-cols-[6rem_1fr] gap-3">
              <dt className="meta-label pt-0.5">Format</dt>
              <dd>{business.discoveryMinutes}-minute call</dd>
            </div>
            <div className="grid grid-cols-[6rem_1fr] gap-3">
              <dt className="meta-label pt-0.5">Outcome</dt>
              <dd>A recommended next step—which may or may not be a pilot</dd>
            </div>
          </dl>
        </div>

        <div className="surface surface-lit p-6 sm:p-8">
          <StepIndicator step={step} />

          {step === 1 && (
            <form onSubmit={goToStep2} noValidate className="space-y-6">
              <h2 ref={formTop} tabIndex={-1} className="text-3xl focus:outline-none">
                Your workflow
              </h2>
              {errorCount > 0 && (
                <p role="alert" className="border border-victorycoral/40 bg-victorycoral/10 px-4 py-3 text-sm">
                  Please fix {errorCount === 1 ? 'the highlighted field' : `the ${errorCount} highlighted fields`}{' '}
                  below.
                </p>
              )}

              <div className="grid gap-6 sm:grid-cols-2">
                <Field id="name" label="Name" error={errors.name}>
                  <input
                    id="name"
                    autoComplete="name"
                    className={inputClass}
                    value={details.name}
                    onChange={(e) => set('name', e.target.value)}
                    aria-invalid={!!errors.name}
                    aria-describedby={describedBy('name', errors.name)}
                    required
                    maxLength={200}
                  />
                </Field>
                <Field id="email" label="Work email" error={errors.email}>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={inputClass}
                    value={details.email}
                    onChange={(e) => set('email', e.target.value)}
                    aria-invalid={!!errors.email}
                    aria-describedby={describedBy('email', errors.email)}
                    required
                    maxLength={320}
                  />
                </Field>
              </div>

              <Field id="businessName" label="Business name" error={errors.businessName}>
                <input
                  id="businessName"
                  autoComplete="organization"
                  className={inputClass}
                  value={details.businessName}
                  onChange={(e) => set('businessName', e.target.value)}
                  aria-invalid={!!errors.businessName}
                  aria-describedby={describedBy('businessName', errors.businessName)}
                  required
                  maxLength={200}
                />
              </Field>

              <Field id="interest" label="Closest area of interest" hint="Change this if it doesn’t fit.">
                <select
                  id="interest"
                  className={inputClass}
                  value={details.interest}
                  onChange={(e) => set('interest', e.target.value as InterestId)}
                  aria-describedby="interest-hint"
                >
                  {interestOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                id="workflow"
                label="What would you like an assistant to handle?"
                error={errors.workflow}
                hint="A short answer is fine. Please don’t include passwords or sensitive customer information."
              >
                <textarea
                  id="workflow"
                  rows={4}
                  className={inputClass}
                  value={details.workflow}
                  onChange={(e) => set('workflow', e.target.value)}
                  placeholder="e.g. We get 30+ inquiries a week and someone has to sort them and write the first reply."
                  aria-invalid={!!errors.workflow}
                  aria-describedby={describedBy('workflow', errors.workflow, true)}
                  required
                  maxLength={2000}
                />
              </Field>

              <div className="grid gap-6 sm:grid-cols-2">
                <Field id="website" label="Website" optional>
                  <input
                    id="website"
                    type="url"
                    inputMode="url"
                    autoComplete="url"
                    className={inputClass}
                    placeholder="https://"
                    value={details.website}
                    onChange={(e) => set('website', e.target.value)}
                    maxLength={500}
                  />
                </Field>
                <Field id="currentTools" label="Current tools" optional>
                  <input
                    id="currentTools"
                    className={inputClass}
                    placeholder="e.g. Gmail, HubSpot, Google Drive"
                    value={details.currentTools}
                    onChange={(e) => set('currentTools', e.target.value)}
                    maxLength={1000}
                  />
                </Field>
              </div>

              <label className="flex items-start gap-3 text-sm text-mist">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-goldcore"
                  checked={details.marketingConsent}
                  onChange={(e) => set('marketingConsent', e.target.checked)}
                />
                <span>Optional: send me occasional GoldCrusade updates. You’ll receive booking emails either way.</span>
              </label>

              <button type="submit" className="gc-btn gc-btn-primary w-full sm:w-auto" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Choose a time
                {!submitting && (
                  <span className="gc-arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </button>
            </form>
          )}

          {step === 2 && useScheduler && (
            <div className="space-y-5">
              <h2 ref={formTop} tabIndex={-1} className="text-3xl focus:outline-none">
                Choose a time
              </h2>
              <p className="text-mist">
                {business.discoveryMinutes}-minute discovery call. Times are shown in your timezone ({detectedTz}).
              </p>
              <iframe
                title="Schedule your discovery call"
                src={schedulerSrc(details)}
                className="h-[640px] w-full border border-rule bg-white"
              />
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 text-mist hover:text-goldcore"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Edit details
                </button>
                <button type="button" onClick={() => setUseScheduler(false)} className="text-link">
                  No time works? Send preferred availability instead
                </button>
              </div>
            </div>
          )}

          {step === 2 && !useScheduler && (
            <form onSubmit={submitRequest} noValidate className="space-y-7">
              <div>
                <h2 ref={formTop} tabIndex={-1} className="mb-2 text-3xl focus:outline-none">
                  Choose a time
                </h2>
                <p className="text-mist">
                  Tell us when suits you for a {business.discoveryMinutes}-minute call. This is a request—we’ll reply by
                  email to confirm a specific time.
                </p>
              </div>

              <fieldset aria-describedby={availabilityError ? 'availability-error' : undefined}>
                <legend className="mb-3 text-sm font-medium">Preferred days</legend>
                <div id="availability-days" tabIndex={-1} className="flex flex-wrap gap-2 focus:outline-none">
                  {DAYS.map((d) => (
                    <label
                      key={d}
                      className={cn(
                        'cursor-pointer border px-3.5 py-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-goldcore',
                        days.includes(d) ? 'border-goldcore text-goldcore' : 'border-rule2 hover:border-mist/50',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={days.includes(d)}
                        onChange={() => toggle(days, d, setDays)}
                      />
                      {d}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-3 text-sm font-medium">Preferred times</legend>
                <div className="flex flex-wrap gap-2">
                  {TIMES.map((t) => (
                    <label
                      key={t}
                      className={cn(
                        'cursor-pointer border px-3.5 py-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-goldcore',
                        times.includes(t) ? 'border-goldcore text-goldcore' : 'border-rule2 hover:border-mist/50',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={times.includes(t)}
                        onChange={() => toggle(times, t, setTimes)}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </fieldset>

              {availabilityError && (
                <p id="availability-error" role="alert" className="text-sm text-victorycoral">
                  {availabilityError}
                </p>
              )}

              <Field id="timezone" label="Your timezone">
                {zones.length > 1 ? (
                  <select
                    id="timezone"
                    className={inputClass}
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {zones.map((z) => (
                      <option key={z} value={z}>
                        {z.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="timezone"
                    className={inputClass}
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    maxLength={64}
                  />
                )}
              </Field>

              <Field id="notes" label="Anything else about timing?" optional>
                <textarea
                  id="notes"
                  rows={3}
                  className={inputClass}
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setAvailabilityError('');
                  }}
                  placeholder="e.g. Not available the week of the 14th."
                  maxLength={1000}
                />
              </Field>

              {submitError && (
                <p role="alert" className="border border-victorycoral/40 bg-victorycoral/10 px-4 py-3 text-sm">
                  {submitError}
                </p>
              )}

              <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 text-sm text-mist hover:text-goldcore"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Edit details
                </button>
                <button type="submit" className="gc-btn gc-btn-primary" disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {submitting ? 'Sending request…' : 'Request a discovery call'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Start;
